import { useState, useEffect } from "react";

import { forgotPassword } from "@/api/authentication";

import EmailErrorPage from "@/components/widget/email-error-page";
import EmailSentConfirmation from "@/components/widget/email-sent-confirmation";

import { AuthInput } from "@/components/ui/auth-input";
import { Button } from "@/components/ui/button";

// Styles Constants based on Figma
const STYLES = {
  title: {
    fontSize: "20px",
    lineHeight: "20px",
    color: "#002248"
  },
  description: {
    fontSize: "12px",
    lineHeight: "20px",
    color: "#000000"
  },
  button: {
    width: "108px",
    height: "32px",
    padding: "7px 0",
    background: "#0068B8",
    boxShadow: "0px 10px 10px rgba(0, 0, 0, 0.25)",
    fontSize: "15px",
    lineHeight: "15px",
    color: "#FFF8F8",
    borderRadius: "4px"
  }
} as const;

// متن ثابت دیفالت که بعد از پیام بکند نمایش داده می‌شود
const DEFAULT_SUFFIX_MESSAGE =
  "لطفاً صحت آدرس وارد شده را بررسی نموده و در صورت نیاز، مجدداً تلاش فرمایید.\nدر صورت ادامه مشکل، با پشتیبانی سامانه تماس حاصل نمایید.";

interface ForgotPasswordProps {
  onBack: () => void;
}

const ForgotPassword = ({ onBack }: ForgotPasswordProps) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showErrorPage, setShowErrorPage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [errorKey, setErrorKey] = useState(0); // برای ریست کردن انیمیشن

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage(null);

    try {
      const result = await forgotPassword(email);

      if (result && result.status) {
        // نمایش کامپوننت تأیید ارسال ایمیل
        setShowConfirmation(true);
      } else {
        // ترکیب پیام بکند با متن ثابت
        const backendMessage =
          result?.message ||
          "پست الکترونیکی وارد شده در سامانه ثبت نشده یا اشتباه است.";
        const fullErrorMessage = `${backendMessage}\n${DEFAULT_SUFFIX_MESSAGE}`;
        setErrorMessage(fullErrorMessage);
        setShowErrorPage(true);
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      // خطا در ارتباط با سرور
      const fullErrorMessage = `خطایی رخ داده است. لطفا مجددا تلاش فرمایید.\n${DEFAULT_SUFFIX_MESSAGE}`;
      setErrorMessage(fullErrorMessage);
      setShowErrorPage(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    setIsSubmitting(true);
    setMessage(null);

    try {
      const result = await forgotPassword(email);
      if (!result || !result.status) {
        setMessage({
          type: "error",
          text: result?.message || "خطا در ارسال مجدد. لطفاً دوباره تلاش کنید"
        });
        setErrorKey((prev) => prev + 1); // ریست انیمیشن
      }
    } catch (error) {
      console.error("Resend email error:", error);
      setMessage({
        type: "error",
        text: "خطایی رخ داده است. لطفاً دوباره تلاش کنید"
      });
      setErrorKey((prev) => prev + 1); // ریست انیمیشن
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToForm = () => {
    setShowConfirmation(false);
    setShowErrorPage(false);
    setEmail("");
    setMessage(null);
    setErrorMessage("");
  };

  const handleBackFromError = () => {
    setShowErrorPage(false);
    setErrorMessage("");
    setMessage(null);
  };

  // اگر خطایی رخ داده، صفحه خطا را نمایش بده
  if (showErrorPage) {
    return (
      <EmailErrorPage
        errorMessage={errorMessage}
        onBack={handleBackFromError}
      />
    );
  }

  // اگر ایمیل با موفقیت ارسال شده، کامپوننت تأیید را نمایش بده
  if (showConfirmation) {
    return (
      <EmailSentConfirmation
        onResend={handleResend}
        onBack={handleBackToForm}
      />
    );
  }

  return (
    <div className="w-full space-y-4 sm:space-y-5">
      <Header />
      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
        <EmailField value={email} onChange={setEmail} />
        {message && (
          <MessageAlert
            type={message.type}
            text={message.text}
            errorKey={errorKey}
          />
        )}
        <ActionButtons isSubmitting={isSubmitting} onBack={onBack} />
      </form>
    </div>
  );
};

const Header = () => (
  <div className="space-y-3 sm:space-y-4">
    <Title />
    <Description />
  </div>
);

const Title = () => (
  <h2
    className="flex justify-center text-right font-normal"
    style={{
      ...STYLES.title,
      height: "18px"
    }}
  >
    فراموشی رمز عبور
  </h2>
);

const Description = () => (
  <p
    className="flex items-center text-right font-normal"
    style={STYLES.description}
  >
    لطفاً جهت بازیابی رمز عبور، پست الکترونیکی ثبت شده در سامانه را وارد نمایید.
  </p>
);

interface EmailFieldProps {
  value: string;
  onChange: (value: string) => void;
}

const EmailField = ({ value, onChange }: EmailFieldProps) => (
  <AuthInput
    label="پست الکترونیکی"
    name="email"
    type="email"
    labelWidth="95px"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    autoComplete="email"
  />
);

interface MessageAlertProps {
  type: "success" | "error";
  text: string;
  errorKey?: number;
}

const MessageAlert = ({ type, text, errorKey = 0 }: MessageAlertProps) => {
  const [isShaking, setIsShaking] = useState(false);

  useEffect(() => {
    if (type === "error") {
      setIsShaking(true);
      const timer = setTimeout(() => setIsShaking(false), 650);
      return () => clearTimeout(timer);
    }
  }, [type, errorKey]);

  return (
    <div
      className={`rounded-lg p-3 text-center text-sm transition-all ${
        type === "success"
          ? "bg-green-50 text-green-700"
          : "bg-red-50 text-red-700"
      } ${isShaking ? "animate-shake" : ""}`}
      role="alert"
    >
      {text}
    </div>
  );
};

interface ActionButtonsProps {
  isSubmitting: boolean;
  onBack: () => void;
}

const ActionButtons = ({ isSubmitting, onBack }: ActionButtonsProps) => (
  <div className="flex items-center justify-between gap-2">
    <BackToDashboardLink onClick={onBack} />
    <Button
      type="submit"
      disabled={isSubmitting}
      className="flex items-center justify-center font-normal transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
      style={STYLES.button}
    >
      {isSubmitting ? "در حال ارسال..." : "ارسال"}
    </Button>
  </div>
);

interface BackToDashboardLinkProps {
  onClick: () => void;
}

const BackToDashboardLink = ({ onClick }: BackToDashboardLinkProps) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-1 text-sm transition-opacity hover:opacity-80"
    style={{
      color: "#0068B8",
      fontSize: "14px"
    }}
  >
    <span>→</span>
    <span>بازگشت</span>
  </button>
);

export default ForgotPassword;
